import { useT } from '@/i18n/LocaleProvider';

import type { AccountResponse } from './api';

export type SidebarPendingProposal = {
  id: string;
  label: string;
  status: string;
};

export type AuthPanelProps = {
  account?: AccountResponse | null;
  canModerate?: boolean;
  pendingProposals?: SidebarPendingProposal[];
  variant?: 'sidebar' | 'bar';
  onLogin: () => void;
  onLogout: () => void;
  onModeration?: () => void;
  onPasswordChange?: () => void;
  onPropose: () => void;
  onRegister: () => void;
};

export function AuthPanel({
  account,
  canModerate = false,
  pendingProposals = [],
  variant = 'sidebar',
  onLogin,
  onLogout,
  onModeration,
  onPasswordChange,
  onPropose,
  onRegister,
}: AuthPanelProps) {
  const t = useT();
  const className = variant === 'bar' ? 'sidebar-auth sidebar-auth--bar' : 'sidebar-auth';

  if (!account) {
    return (
      <section className={className} aria-label={t('auth_panel_label')}>
        {variant === 'sidebar' ? (
          <div className="sidebar-auth-copy">
            <strong>{t('auth_panel_label')}</strong>
            <span>{t('auth_login_to_propose')}</span>
          </div>
        ) : null}
        <div className="sidebar-auth-actions">
          <button type="button" onClick={onLogin}>
            {t('auth_login')}
          </button>
          <button type="button" onClick={onRegister}>
            {t('auth_register')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className={className} aria-label={t('auth_panel_label')}>
      <div className="sidebar-auth-copy">
        <strong>{account.email}</strong>
        {variant === 'sidebar' ? <span>{account.role_id}</span> : null}
      </div>

      <div className="sidebar-auth-actions">
        {canModerate && onModeration ? (
          <button type="button" onClick={onModeration}>
            {t('auth_moderation')}
          </button>
        ) : null}
        <button type="button" onClick={onPropose}>
          {t('auth_propose_edit')}
        </button>
        {onPasswordChange ? (
          <button type="button" onClick={onPasswordChange}>
            {t('auth_change_password')}
          </button>
        ) : null}
        <button type="button" onClick={onLogout}>
          {t('auth_logout')}
        </button>
      </div>

      {variant === 'sidebar' && pendingProposals.length > 0 ? (
        <div className="pending-summary" data-testid="pending-summary">
          <span>{t('auth_pending_count', { count: String(pendingProposals.length) })}</span>
          <ul>
            {pendingProposals.slice(0, 3).map((proposal) => (
              <li key={proposal.id}>{proposal.label}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
